import { Suspense, lazy, useEffect, useState, useSyncExternalStore } from 'react';
import { DocPanel } from '../../components/DocPanel/DocPanel';
import { fetchAttachmentText, type PreviewDoc } from '../../lib/preview-doc';
import { tBoards, getBoardsLang, subscribeBoardsLang } from '../../lib/boards-i18n';

/**
 * 게시글 첨부 문서뷰어 (2026-08-18).
 *
 * 렌더러는 **채팅 미리보기의 것을 그대로** 쓴다(수정 0) — WorkspaceDocPanel 이 이미 같은
 * 방식으로 재사용한다. 채팅의 `chatPreviewStore` 는 roomId 로 스코프돼 있어 게시판에서
 * 그대로 쓸 수 없다. 그래서 스토어는 건드리지 않고 DocPanel 껍데기 + 렌더러만 조합한다.
 *
 * 열 수 없는 형식(zip 등)은 애초에 버튼을 그리지 않는다 — 판정은 attachmentPreviewDoc 이 한다.
 */

const DocxPreview = lazy(() => import('../ChatPage/ChatArea/preview/DocxPreview'));
const XlsxPreview = lazy(() => import('../ChatPage/ChatArea/preview/XlsxPreview'));
const PptxPreview = lazy(() => import('../ChatPage/ChatArea/preview/PptxPreview'));
const PdfPreview = lazy(() => import('../ChatPage/ChatArea/preview/PdfPreview'));
const HwpPreview = lazy(() => import('../ChatPage/ChatArea/preview/HwpPreview'));

interface BoardDocViewerProps {
  doc: PreviewDoc;
  onClose: () => void;
}

export function BoardDocViewer({ doc, onClose }: BoardDocViewerProps) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const isText = doc.kind === 'markdown' || doc.kind === 'html';
  return (
    <div className="bd-viewer">
      <DocPanel title={doc.title} onClose={onClose}>
        <Suspense fallback={<p className="bd-muted">{tBoards('boards.viewer.loading')}</p>}>
          {doc.kind === 'pdf' && <PdfPreview doc={doc} />}
          {doc.kind === 'docx' && <DocxPreview doc={doc} />}
          {doc.kind === 'xlsx' && <XlsxPreview doc={doc} />}
          {doc.kind === 'pptx' && <PptxPreview doc={doc} />}
          {doc.kind === 'hwp' && <HwpPreview doc={doc} />}
          {isText && <TextDoc doc={doc} />}
        </Suspense>
      </DocPanel>
    </div>
  );
}

/**
 * markdown/html 은 전용 렌더러가 채팅 말풍선 쪽 로직에 묶여 있다. 여기서는 원문을 그대로
 * 보여준다 — html 을 innerHTML 로 붙이지 않는 것이 요점이다(게시판 첨부는 누구나 올린다).
 */
function TextDoc({ doc }: { doc: PreviewDoc }) {
  const fileId = doc.source.kind === 'attachment' ? doc.source.fileId : null;
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    setText(null);
    fetchAttachmentText(fileId)
      .then((t) => { if (!cancelled) setText(t); })
      .catch(() => { if (!cancelled) setText(''); });
    return () => { cancelled = true; };
  }, [fileId]);

  if (text === null) return <p className="bd-muted">{tBoards('boards.viewer.loading')}</p>;
  return <pre className="bd-viewer__text">{text}</pre>;
}
